import {
	BUSINESS_TYPES,
	DEALERSHIP_AGE,
	JOB_FUNCTION,
	PHONE_TYPES,
	VEHICLES_SOLD_MONTHLY,
} from 'partials/sign_up/data';
import { v4 } from 'uuid';
import files from 'helpers/files';
import { isAfcOktaUser, isAffiliatePartner, isFastPathSignUp } from 'partials/sign_up/helpers';

const getRailsBusinessType = (value) => getRailsValue(BUSINESS_TYPES, value, 0);
const getRailsJobFunction = (value) => getRailsValue(JOB_FUNCTION, value, 1);
export const getRailsPhoneType = (value) => getRailsValue(PHONE_TYPES, value, 0);

const getRailsValue = (options, value, defaultRailsValue) => {
	const intValue = parseInt(value);
	const option = options.find((opt) => opt.value === intValue);
	return option ? option.railsValue : defaultRailsValue;
};

export const getRailsValueForRadioButton = (value, options, defaultRailsValue) => {
	const option = options.find((option) => option.value == value);
	return option ? option.railsValue : defaultRailsValue;
};

const getRailsDealershipAge = (value) => {
	const dealershipAge = DEALERSHIP_AGE.find((age) => age.value == value);
	return dealershipAge ? dealershipAge.railsValue : 0;
};

export const getAnonymousUser = () => {
	return { user_id: v4() };
};

const isDealershipAuctionAccessId = (auctionAccessId) => auctionAccessId.startsWith('5') && auctionAccessId.length == 7;
const isUserAuctionAccessId = (auctionAccessId) => auctionAccessId.startsWith('1') && auctionAccessId.length == 9;

const buildStepZero = async (values, onboardingLanguage, query) => {
	const isAFCSignup = isAfcOktaUser(query);
	const isAffiliatePartnerSignup = isAffiliatePartner(query);
	const isOneSingleStepSignUp = isAFCSignup || isFastPathSignUp(query) || isAffiliatePartnerSignup;
	const auctionAccessId = values.auctionAccessDealerId?.trim() || '';
	const marketplace_document_ids = [];
	for (const key in values) {
		if (typeof values[key] === 'object' && values[key]?.hasOwnProperty('id')) {
			marketplace_document_ids.push(values[key].id);
		}
	}
	const body = {
		user: {
			first_name: values.firstName,
			last_name: values.lastName,
			legal_business_name: values.legalBusinessName,
			email: values.email,
			dealer_type: 0,
			dealership_name: values.dealershipName,
			has_us_license: values.hasUSLicense === 'yes',
			partial_sign_up_attributes: {
				last_step: 0,
			},
			user_profile_attributes: {
				mobile_phone: values.mobilePhone,
				declared_business_type: getRailsBusinessType(values.businessType),
				...(isOneSingleStepSignUp ? { phone: values.mobilePhone } : {}),
			},
			auction_access_dealer_id: isDealershipAuctionAccessId(auctionAccessId) ? auctionAccessId : '',
			auction_access_person_id: isUserAuctionAccessId(auctionAccessId) ? auctionAccessId : '',
			onboarding_language: onboardingLanguage,
			marketplace_document_ids,
			// on fast path signup we need to send the address in the first step
			...(isOneSingleStepSignUp ? buildAddress(values) : {}),
			// add tax identification number for afc sign ups in the first step
			...(isAFCSignup ? buildLegalAttributes(values.stateTaxId) : {}),
		},
	};

	const driverLicenseFile = values.driverLicenseFile;
	if (driverLicenseFile?.length && driverLicenseFile[0].size) {
		body.user.user_profile_attributes.drivers_license_file = await files.toBase64(driverLicenseFile[0]);
	}

	return body;
};

const unknownMonthlySales = VEHICLES_SOLD_MONTHLY.find((option) => option.text === 'Unknown').value;

const buildAddress = (values) => {
	const address = {
		street: values.dealershipAddress,
		street_line_two: values.dealershipAddress2,
		city: values.city,
		state: values.state,
		zipcode: values.zip,
		category: 'dealership',
	};

	// Only include lat/lng if they're numbers (initial state has them as empty strings)
	if (typeof values.latitude === 'number') {
		address.latitude = values.latitude;
	}
	if (typeof values.longitude === 'number') {
		address.longitude = values.longitude;
	}

	return {
		addresses_attributes: {
			0: address,
		},
	};
};

const buildStepOne = (values) => ({
	user: {
		user_profile_attributes: {
			declared_time_in_business: getRailsDealershipAge(values.dealershipAge),
			declared_monthly_sales: unknownMonthlySales,
			job_title: getRailsJobFunction(values.jobFunction),
			job_title_description: values.jobFunctionDescription,
			phone: values.dealershipPhone,
		},
		...buildAddress(values),
		partial_sign_up_attributes: {
			last_step: 1,
		},
	},
});

const buildStepTwo = async (values) => {
	const auctionAccessDealerId = values.auctionAccessDealerId || values.auction_access_dealer_id;
	const body = {
		user: {
			user_profile_attributes: {},
			...buildLegalAttributes(values.taxID),
			...(auctionAccessDealerId ? { auction_access_dealer_id: auctionAccessDealerId } : {}),
			partial_sign_up_attributes: {
				last_step: 2,
			},
		},
	};

	const dealerLicenseFile = values.dealerLicenseFile;
	if (dealerLicenseFile?.length && dealerLicenseFile[0].size) {
		body.user.user_profile_attributes.dealers_license_file = await files.toBase64(dealerLicenseFile[0]);
	}

	const driverLicenseFile = values.driverLicenseFile;
	if (driverLicenseFile?.length && driverLicenseFile[0].size) {
		body.user.user_profile_attributes.drivers_license_file = await files.toBase64(driverLicenseFile[0]);
	}

	return body;
};

const buildLegalAttributes = (taxID) => ({
	user_legal_attributes: {
		tax_identification_number: taxID,
	},
});

const getPaymentMethodSelection = (values) => {
	const hasPaymentMethodSelection =
		values.paymentMethods ||
		Object.prototype.hasOwnProperty.call(values, 'buyingCandidateId') ||
		Object.prototype.hasOwnProperty.call(values, 'sellingCandidateId') ||
		Object.prototype.hasOwnProperty.call(values, 'floorPlanCandidateIds');
	if (!hasPaymentMethodSelection) return undefined;

	const paymentMethods = values.paymentMethods || values;
	const buyingCandidateId = paymentMethods.buyingCandidateId;
	const sellingCandidateId = paymentMethods.sellingCandidateId;
	const floorPlanCandidateIds = paymentMethods.floorPlanCandidateIds || [];

	return { buyingCandidateId, sellingCandidateId, floorPlanCandidateIds };
};

const buildStepThree = (values) => {
	const paymentMethods = getPaymentMethodSelection(values);

	return {
		user: {
			password: values.password,
			password_confirmation: values.passwordConfirmation,
			partial_sign_up_attributes: {
				last_step: 3,
				...(paymentMethods ? { paymentMethods } : {}),
			},
		},
	};
};

const buildUtmProperties = (query) => ({
	utm_campaign: query.utm_campaign,
	utm_source: query.utm_source,
	utm_medium: query.utm_medium,
	utm_content: query.utm_content,
	utm_term: query.utm_term,
});

const buildTrackingProperties = (query) => ({
	...buildUtmProperties(query),
	site_referral: query.site_referral,
});

const build = async (
	step,
	values,
	sign_up_token,
	sign_up_email,
	query,
	onboardingLanguage,
	resumeStep,
	recaptchaToken,
	signUpProperties,
) => {
	const buildArray = [buildStepZero, buildStepOne, buildStepTwo, buildStepThree];

	if (sign_up_email === '' && values.email !== '') {
		sign_up_email = values.email;
	}

	let body = await buildArray[step](values, onboardingLanguage, query);
	body = {
		...body,
		sign_up_token,
		sign_up_email,
		affiliate_partner: query.affiliate_partner,
		pl_okta: query.pl_okta,
		afc_user_id: query.afcuserid,
		afc_dealer_id: query.afcdealerid,
		path_id: query.pathid,
		...buildTrackingProperties(query),
	};

	if (resumeStep) {
		body.user.partial_sign_up_attributes.resume_step = resumeStep;
	}

	if (step === 0 && recaptchaToken) {
		body.user.partial_sign_up_attributes.recaptcha_token = recaptchaToken;
	}

	Object.assign(body.user.partial_sign_up_attributes, signUpProperties);

	return body;
};

export const buildSellersSignUpUrl = (query) => {
	const baseUrl = 'https://openlane.typeform.com/to/Ee3lmcvq';
	const referrerToken = query && query.utm_campaign;
	const queryString = referrerToken ? `?referrer_token=${referrerToken}` : '';
	return baseUrl + queryString;
};

const signup = { build };

export default signup;
